import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon, Edit02Icon, PlusSignIcon } from "@hugeicons/core-free-icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  useCompanyApiKeys,
  useCreateCompanyApiKey,
  useDeleteCompanyApiKey,
  useRevealCompanyApiKey,
  useUpdateCompanyApiKey,
} from "../hooks/useCompanyApiKeys";
import {
  API_KEY_PROVIDERS,
  CUSTOM_PROVIDER_SPEC,
  REQUIRED_PROVIDERS,
  findProviderSpec,
} from "../constants/providerCatalog";
import type { ApiKeyProviderSpec, ApiKeySlot } from "../constants/providerCatalog";
import type { CompanyApiKeyListItem } from "../types/companyApiKey.types";

type SlotValues = Record<ApiKeySlot, string>;

const EMPTY_SLOTS: SlotValues = { key: "", subKey1: "", subKey2: "" };

interface CompanyApiKeyPanelProps {
  companyGuid: string;
}

export function CompanyApiKeyPanel({ companyGuid }: CompanyApiKeyPanelProps) {
  const { data, isLoading } = useCompanyApiKeys(companyGuid);
  const createMutation = useCreateCompanyApiKey(companyGuid);
  const updateMutation = useUpdateCompanyApiKey(companyGuid);
  const deleteMutation = useDeleteCompanyApiKey(companyGuid);
  const revealMutation = useRevealCompanyApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CompanyApiKeyListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CompanyApiKeyListItem | null>(null);

  const [specName, setSpecName] = useState(API_KEY_PROVIDERS[0]?.name ?? "");
  const [customProvider, setCustomProvider] = useState("");
  const [customName, setCustomName] = useState("");
  const [slots, setSlots] = useState<SlotValues>(EMPTY_SLOTS);
  const [description, setDescription] = useState("");

  const items = useMemo(() => data?.items ?? [], [data]);

  /** 이미 등록된 name 은 (업체, name) 유니크라 다시 못 만든다 — 목록에서 제외해 혼선을 막는다. */
  const registeredNames = useMemo(
    () => new Set(items.map((i) => i.name.toUpperCase())),
    [items],
  );

  /** 공용 폴백이 없는데 이 업체에 아직 키가 없는 제공자 — 해당 기능이 이 업체에서 실패한다. */
  const missingRequired = useMemo(
    () => (isLoading ? [] : REQUIRED_PROVIDERS.filter((p) => !registeredNames.has(p.name))),
    [isLoading, registeredNames],
  );

  const activeSpec: ApiKeyProviderSpec = useMemo(
    () => API_KEY_PROVIDERS.find((p) => p.name === specName) ?? CUSTOM_PROVIDER_SPEC,
    [specName],
  );

  const resetForm = useCallback(() => {
    setSpecName(API_KEY_PROVIDERS[0]?.name ?? "");
    setCustomProvider("");
    setCustomName("");
    setSlots(EMPTY_SLOTS);
    setDescription("");
  }, []);

  const setSlot = useCallback((slot: ApiKeySlot, value: string) => {
    setSlots((prev) => ({ ...prev, [slot]: value }));
  }, []);

  const handleCreate = useCallback(async () => {
    const isCustom = activeSpec === CUSTOM_PROVIDER_SPEC;
    const provider = isCustom ? customProvider.trim() : activeSpec.provider;
    const name = isCustom ? customName.trim() : activeSpec.name;

    if (!name) {
      toast.error("name 을 입력하세요.");
      return;
    }
    if (registeredNames.has(name.toUpperCase())) {
      toast.error(`이미 등록된 항목입니다 (${name}). 수정으로 값을 바꾸세요.`);
      return;
    }
    const missing = activeSpec.fields.find((f) => f.required && !slots[f.slot].trim());
    if (missing) {
      toast.error(`${missing.label} 은(는) 필수입니다.`);
      return;
    }

    await createMutation.mutateAsync({
      CompanyGuid: companyGuid,
      ApiProvider: provider,
      Name: name,
      Key: slots.key.trim(),
      SubKey1: slots.subKey1.trim() || null,
      SubKey2: slots.subKey2.trim() || null,
      Description: description.trim() || null,
    });

    setCreateOpen(false);
    resetForm();
  }, [
    activeSpec,
    companyGuid,
    createMutation,
    customName,
    customProvider,
    description,
    registeredNames,
    resetForm,
    slots,
  ]);

  const openEdit = useCallback(
    async (item: CompanyApiKeyListItem) => {
      // 마스킹된 값을 그대로 저장하면 키가 망가지므로, 편집 전에 평문을 받아 채운다.
      const secret = await revealMutation.mutateAsync(item.idx).catch(() => null);
      if (!secret) return;
      setSlots({
        key: secret.key ?? "",
        subKey1: secret.subKey1 ?? "",
        subKey2: secret.subKey2 ?? "",
      });
      setDescription(item.description ?? "");
      setEditTarget(item);
    },
    [revealMutation],
  );

  const handleUpdate = useCallback(async () => {
    if (!editTarget) return;
    const spec = findProviderSpec(editTarget.name);
    const missing = spec.fields.find((f) => f.required && !slots[f.slot].trim());
    if (missing) {
      toast.error(`${missing.label} 은(는) 필수입니다.`);
      return;
    }

    await updateMutation.mutateAsync({
      idx: editTarget.idx,
      req: {
        Key: slots.key.trim(),
        SubKey1: slots.subKey1.trim(),
        SubKey2: slots.subKey2.trim(),
        Description: description,
      },
    });

    setEditTarget(null);
    setSlots(EMPTY_SLOTS);
  }, [description, editTarget, slots, updateMutation]);

  const editSpec = editTarget ? findProviderSpec(editTarget.name) : null;

  return (
    <div className="flex flex-col gap-2 overflow-y-auto p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">외부 API 키</h4>
          <p className="text-xs text-gray-500">
            이 업체 전용 인증정보. 대부분 미등록 시 전사 공용 키로 동작하지만, 공용 키가 없는
            제공자는 등록해야만 동작한다.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            resetForm();
            setCreateOpen(true);
          }}
          className="h-7 shrink-0 bg-blue-600 px-2 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={14} className="mr-1" />
          키 등록
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="w-full">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">제공자</th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">값</th>
              <th className="h-10 px-3 text-left text-xs font-medium uppercase text-gray-500">수정일</th>
              <th className="h-10 px-3 text-right text-xs font-medium uppercase text-gray-500"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-400">
                  로딩 중...
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-400">
                  등록된 업체 전용 키가 없습니다.
                </td>
              </tr>
            )}
            {items.map((item) => {
              const spec = findProviderSpec(item.name);
              const isKnown = spec !== CUSTOM_PROVIDER_SPEC;
              return (
                <tr key={item.idx} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="px-3 py-2 align-top">
                    <div className="text-xs font-medium text-gray-800">
                      {isKnown ? spec.label : item.name}
                    </div>
                    <div className="font-mono text-[11px] text-gray-400">{item.name}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-col gap-0.5">
                      <MaskedRow
                        label={spec.fields[0]?.label ?? "key"}
                        value={item.keyMasked}
                      />
                      {item.hasSubKey1 && (
                        <MaskedRow
                          label={spec.fields[1]?.label ?? "sub_key1"}
                          value={item.subKey1Masked}
                        />
                      )}
                      {item.hasSubKey2 && (
                        <MaskedRow
                          label={spec.fields[2]?.label ?? "sub_key2"}
                          value={item.subKey2Masked}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-gray-500">
                    {item.updateDate
                      ? format(new Date(item.updateDate), "yyyy-MM-dd")
                      : item.registDate
                        ? format(new Date(item.registDate), "yyyy-MM-dd")
                        : "-"}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(item)}
                        disabled={revealMutation.isPending}
                        className="h-7 px-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        <HugeiconsIcon icon={Edit02Icon} size={14} className="mr-1" />
                        수정
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(item)}
                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <HugeiconsIcon icon={Delete02Icon} size={14} className="mr-1" />
                        삭제
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missingRequired.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {missingRequired.map((p) => (
            <p key={p.name}>
              <span className="font-semibold">{p.label}</span> 키 미등록 — 공용 키로 대체되지
              않아 이 업체에서는 동작하지 않습니다.
            </p>
          ))}
        </div>
      )}

      {/* 등록 */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>업체 API 키 등록</DialogTitle>
            <DialogDescription>{activeSpec.description}</DialogDescription>
          </DialogHeader>

          <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">제공자</label>
              <select
                value={specName}
                onChange={(e) => {
                  setSpecName(e.target.value);
                  setSlots(EMPTY_SLOTS);
                }}
                className="h-8 w-full rounded-md border border-gray-300 px-2 text-xs outline-none focus:border-blue-500"
              >
                {API_KEY_PROVIDERS.map((p) => (
                  <option key={p.name} value={p.name} disabled={registeredNames.has(p.name)}>
                    {p.label}
                    {registeredNames.has(p.name)
                      ? " (등록됨)"
                      : p.fallback === "none"
                        ? " (필수)"
                        : ""}
                  </option>
                ))}
                <option value="">{CUSTOM_PROVIDER_SPEC.label}</option>
              </select>
            </div>

            {activeSpec === CUSTOM_PROVIDER_SPEC && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    api_provider
                  </label>
                  <Input
                    value={customProvider}
                    onChange={(e) => setCustomProvider(e.target.value)}
                    maxLength={64}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    maxLength={32}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}

            {activeSpec.fields.map((field) => (
              <SlotField
                key={field.slot}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
                hint={field.hint}
                value={slots[field.slot]}
                onChange={(v) => setSlot(field.slot, v)}
              />
            ))}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">비고</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="관리자 메모 (선택)"
                rows={2}
                className="text-xs"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              취소
            </Button>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createMutation.isPending ? "저장 중..." : "등록"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 수정 */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
            setSlots(EMPTY_SLOTS);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editSpec?.label ?? editTarget?.name} 수정</DialogTitle>
            <DialogDescription>
              현재 저장된 값이 그대로 채워져 있습니다. 바꿀 항목만 수정하세요.
            </DialogDescription>
          </DialogHeader>

          <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto py-2">
            {editSpec?.fields.map((field) => (
              <SlotField
                key={field.slot}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
                hint={field.hint}
                value={slots[field.slot]}
                onChange={(v) => setSlot(field.slot, v)}
              />
            ))}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">비고</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="text-xs"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
              취소
            </Button>
            <Button
              type="button"
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 삭제 확인 */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>이 업체 전용 키를 삭제하시겠습니까?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteFallbackMessage(deleteTarget ? findProviderSpec(deleteTarget.name) : null)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteTarget) {
                  await deleteMutation.mutateAsync(deleteTarget.idx);
                  setDeleteTarget(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function deleteFallbackMessage(spec: ApiKeyProviderSpec | null): string {
  switch (spec?.fallback) {
    case "none":
      return `${spec.label}은(는) 공용 키가 없습니다. 삭제하면 이 업체에서 즉시 동작하지 않습니다.`;
    case "shared":
      return "삭제하면 이 업체는 전사 공용 키로 되돌아갑니다. 공용 키가 이 업체에서 동작하지 않는 연동이라면 즉시 실패하게 됩니다.";
    default:
      return "삭제 후 동작은 이 키를 읽는 코드에 따라 다릅니다. 공용 키 폴백 여부를 확인하세요.";
  }
}

interface MaskedRowProps {
  label: string;
  value: string;
}

function MaskedRow({ label, value }: MaskedRowProps) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="shrink-0 text-[11px] text-gray-400">{label}</span>
      <span className="truncate font-mono text-xs text-gray-600">{value || "-"}</span>
    </div>
  );
}

interface SlotFieldProps {
  label: string;
  required: boolean;
  placeholder?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
}

function SlotField({ label, required, placeholder, hint, value, onChange }: SlotFieldProps) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={256}
        className="h-8 font-mono text-xs"
      />
      {hint && <p className="mt-1 text-[11px] leading-tight text-gray-500">{hint}</p>}
    </div>
  );
}
